/**
 * Record a competency sign-off (plan docs/plans/lms-plan.md 7.4, 7.3, 5.5
 * lms_competency_signoffs; Appendix A POST /admin/signoffs; 18.15, 18.22;
 * R18, R20; WP-45).
 *
 * The observer (the acting admin) opens the checklist of the course's CURRENT
 * version (GET /courses/:id/content, content.competency_checklist; the server
 * holds the sign-off to that version and refuses a checklist that does not
 * mark exactly its steps), marks every step met, not met or n/a with an
 * optional note, records the outcome (competent, or not yet: a record too,
 * and the request stays open, 7.4), and signs with the credential re-check:
 * the fixed observer's statement (7.3) and the admin's password, posted once
 * with { _retry: true } (18.22). A wrong password records nothing and says
 * so. The observer is never the learner (the view refuses to open this for
 * one's own request; the server refuses it too).
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { chipCls, fieldCls, primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { apiErrorCode } from '../../../api/client';
import { getCourseContent } from '../../../api/courses';
import { COMPETENCY_STATEMENT, recordSignoff, SIGNOFF_NOTES_MAX, SIGNOFF_STEP_NOTE_MAX } from '../../../api/team';
import type { AdminSignoffRequest, ChecklistResult, RecordSignoffResult, SignoffOutcome } from '../../../api/team';
import { ErrorNote, labelCls, metaCls, microLabelCls, personName, WarningNote } from '../../../components/pickers/shared';
import { LoadFailed, Loading, useAsync } from '../matrix/shared';
import { PasswordField, signingErrorText } from './PasswordField';

export const RESULT_WORDS: Record<ChecklistResult, string> = {
  met: 'Met',
  not_met: 'Not met',
  na: 'N/A',
};

const RESULTS: ChecklistResult[] = ['met', 'not_met', 'na'];

export const OUTCOME_WORDS: Record<SignoffOutcome, string> = {
  competent: 'Competent',
  not_yet: 'Not yet',
};

export interface SignoffDialogProps {
  request: AdminSignoffRequest;
  onCancel: () => void;
  onDone: (result: RecordSignoffResult) => void;
}

export function SignoffDialog({ request, onCancel, onDone }: SignoffDialogProps) {
  const courseId = request.course?.id ?? request.course_id;
  const content = useAsync(() => getCourseContent(courseId), courseId, 'The checklist of this course could not be loaded.');
  const [results, setResults] = useState<Record<string, ChecklistResult>>({});
  const [stepNotes, setStepNotes] = useState<Record<string, string>>({});
  const [outcome, setOutcome] = useState<SignoffOutcome | null>(null);
  const [notes, setNotes] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const steps = content.data?.content?.competency_checklist ?? [];
  const learnerName = request.learner ? personName(request.learner) : 'This person';
  const allMarked = steps.length > 0 && steps.every((s) => results[s.id] !== undefined);
  const notesTooLong = notes.length > SIGNOFF_NOTES_MAX || steps.some((s) => (stepNotes[s.id] ?? '').length > SIGNOFF_STEP_NOTE_MAX);
  const ready = allMarked && outcome !== null && password.length > 0 && !notesTooLong && !busy;
  const anyNotMet = steps.some((s) => results[s.id] === 'not_met');

  const submit = async () => {
    if (!ready || !outcome) return;
    setBusy(true);
    setError(null);
    try {
      const result = await recordSignoff({
        user_id: request.user_id,
        course_id: courseId,
        checklist: steps.map((s) => ({ id: s.id, result: results[s.id], note: stepNotes[s.id] ?? null })),
        outcome,
        notes,
        password,
      });
      setPassword('');
      onDone(result);
    } catch (err) {
      if (apiErrorCode(err) === 'LMS_REAUTH_FAILED') setPassword('');
      setError(signingErrorText(err, 'The sign-off could not be recorded. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="space-y-4"
      data-testid="signoff-dialog"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <p className="text-sm text-gray-700 dark:text-gray-200">
        {`Observe ${learnerName} performing ${request.course?.title ?? 'this skill'}, mark every step, then sign.`}
      </p>

      {content.error ? <LoadFailed message={content.error} onRetry={content.reload} testId="signoff-checklist-error" /> : null}
      {content.loading && !content.data ? <Loading rows={3} /> : null}
      {content.data && steps.length === 0 ? (
        <ErrorNote testId="signoff-no-checklist">
          {"The current version of this course has no competency checklist, so a sign-off cannot be recorded on it."}
        </ErrorNote>
      ) : null}

      {steps.length > 0 ? (
        <ol className="space-y-3" data-testid="signoff-checklist">
          {steps.map((step, i) => (
            <li key={step.id} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700" data-testid={`step-${step.id}`}>
              <fieldset>
                <legend className="text-sm font-medium text-gray-900 dark:text-gray-100">{`${i + 1}. ${step.text}`}</legend>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {RESULTS.map((r) => (
                    <button
                      key={r}
                      type="button"
                      className={chipCls(results[step.id] === r)}
                      aria-pressed={results[step.id] === r}
                      onClick={() => setResults((prev) => ({ ...prev, [step.id]: r }))}
                      data-testid={`step-${step.id}-${r}`}
                    >
                      {RESULT_WORDS[r]}
                    </button>
                  ))}
                </div>
                <label className="sr-only" htmlFor={`step-note-${step.id}`}>
                  {`Note for step ${i + 1}`}
                </label>
                <input
                  id={`step-note-${step.id}`}
                  className={`${fieldCls} mt-2 w-full`}
                  placeholder="Note (optional)"
                  value={stepNotes[step.id] ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setStepNotes((prev) => ({ ...prev, [step.id]: value }));
                  }}
                  data-testid={`step-${step.id}-note`}
                />
              </fieldset>
            </li>
          ))}
        </ol>
      ) : null}

      {steps.length > 0 ? (
        <>
          <div>
            <span className={microLabelCls}>Outcome</span>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {(['competent', 'not_yet'] as SignoffOutcome[]).map((o) => (
                <button
                  key={o}
                  type="button"
                  className={chipCls(outcome === o)}
                  aria-pressed={outcome === o}
                  onClick={() => setOutcome(o)}
                  data-testid={`signoff-outcome-${o}`}
                >
                  {OUTCOME_WORDS[o]}
                </button>
              ))}
            </div>
            {outcome === 'not_yet' ? (
              <p className={metaCls}>A not yet outcome is recorded too; the request stays open and the learner can be observed again.</p>
            ) : null}
            {outcome === 'competent' && anyNotMet ? (
              <WarningNote testId="signoff-not-met-warning">A step is marked not met. Check the outcome before you sign.</WarningNote>
            ) : null}
          </div>
          <div>
            <label className={labelCls} htmlFor="signoff-notes">
              Notes (optional)
            </label>
            <textarea
              id="signoff-notes"
              className={`${fieldCls} w-full`}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="signoff-notes"
            />
            {notesTooLong ? <p className={metaCls}>A note is too long.</p> : null}
          </div>
          <blockquote
            className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200"
            data-testid="signoff-statement"
          >
            {COMPETENCY_STATEMENT}
          </blockquote>
          <PasswordField id="signoff-password" value={password} onChange={setPassword} disabled={busy} testId="signoff-password" />
        </>
      ) : null}

      {error ? <ErrorNote testId="signoff-error">{error}</ErrorNote> : null}

      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={secondaryBtnCls} onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className={primaryBtnCls} disabled={!ready} data-testid="signoff-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
          Sign and record
        </button>
      </div>
    </form>
  );
}

export default SignoffDialog;
